import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Header } from "components/DesignSystem/Header/Header";
import Modal from "components/DesignSystem/Modal/Modal";
import { NoProductAndServices } from "components/Illustrations/NoProductAndServices";
import { AddProductModalContent } from "components/MyProducts/ProductFormModal";
import { BulkProductImport } from "components/MyProducts/BulkProductImport";
import { ProductAndServicesList } from "components/MyProducts/ProductAndServicesList";
import { ArrowDown } from "components/icons/ArrowDown";
import { Import } from "components/icons/Import";
import { PlusIcon } from "components/icons/PlusIcon";
import { useBankConnect } from "hooks/useBankConnect";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetAllConnections } from "hooks/useGetAllConnections";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { ModalControl, useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { LockedUi } from "components/LockedUi/LockedUi";
import { EmptyScreen } from "../EmptyScreen";
import { useGetProductAndServicesQuery } from "store/apis/productAndServices";

const AddProductDropdown = ({
  addProductModal,
  bulkImportModal,
}: {
  addProductModal: ModalControl;
  bulkImportModal: ModalControl;
}) => (
  <LockedUi
    feature={WRITE_PERMISSION_KEYS.REVENUE}
    blockedUi={<LockedButton text="Add product" />}
  >
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group">
        <div>
          <Button size="small" customType="primary">
            Add product
            <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface">
              <ArrowDown color="currentColor" strokeWidth="1.5" />
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={8} side="bottom" align="end">
          <Dropdown.Item
            onSelect={addProductModal.open}
            icon={<PlusIcon size="12" />}
          >
            Create product
          </Dropdown.Item>
          <Dropdown.Item onSelect={bulkImportModal.open} icon={<Import />}>
            Bulk import
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  </LockedUi>
);

export const MyProducts = () => {
  usePageTitle("My Products");
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { isCpa, isForeignCA } = useRoleBasedView();
  const addProductAndServicesModal = useModal();
  const bulkImportModal = useModal();

  const { data, ...productAndServicesState } = useGetProductAndServicesQuery(
    {
      groupId,
      entityId,
      pageNum: 1,
    },
    {
      skip: !entityId || !groupId,
    }
  );

  const { onConnect, isLoading: isConnectBankLoading } = useBankConnect();
  const { stripeConnection, ...connectionState } = useGetAllConnections({
    groupId,
    entityId,
  });

  const isLoading =
    connectionState.isLoading || productAndServicesState.isLoading;
  const isSuccess =
    connectionState.isSuccess && productAndServicesState.isSuccess;

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="My Products"
          right={
            data?.products.length ? (
              <AddProductDropdown
                addProductModal={addProductAndServicesModal}
                bulkImportModal={bulkImportModal}
              />
            ) : undefined
          }
        />
      }
    >
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={data?.products.length === 0}
      >
        <Async.Empty>
          <EmptyScreen
            className="t-h-[70dvh]"
            text=""
            cta={
              <div className="t-flex t-gap-3">
                {stripeConnection &&
                  !stripeConnection?.is_connection_available && (
                    <LockedUi
                      feature={WRITE_PERMISSION_KEYS.REVENUE}
                      blockedUi={<LockedButton text="Connect Stripe" />}
                    >
                      <Button
                        size="small"
                        isLoading={isConnectBankLoading}
                        disabled={isConnectBankLoading}
                        onClick={() =>
                          onConnect({
                            connectionId: stripeConnection?.uuid!,
                            invokedFrom:
                              isCpa || isForeignCA
                                ? `/books/my-products?company=${groupId}&entity=${entityId}`
                                : `/books/my-products?entity=${entityId}`,
                          })
                        }
                      >
                        Connect Stripe
                      </Button>
                    </LockedUi>
                  )}
                <AddProductDropdown
                  addProductModal={addProductAndServicesModal}
                  bulkImportModal={bulkImportModal}
                />
              </div>
            }
          >
            <div className="t-text-center t-flex t-gap-2 t-flex-col t-items-center">
              <NoProductAndServices />
              <div className="t-text-subtitle-sm t-text-text-60">
                No products yet
              </div>
              <div className="t-text-body-sm t-text-text-30">
                Get started by adding them here directly.
              </div>
            </div>
          </EmptyScreen>
        </Async.Empty>
        <Async.Success>
          <ProductAndServicesList />
        </Async.Success>
      </Async.Root>

      <Modal.Root
        open={addProductAndServicesModal.isOpen}
        onOpenChange={addProductAndServicesModal.toggle}
        modal={false}
      >
        <AddProductModalContent {...addProductAndServicesModal} />
      </Modal.Root>
      <BulkProductImport {...bulkImportModal} />
    </DashboardLayout>
  );
};
